'use client'
import { Box, Button, Center, Flex, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { sendGTMEvent } from '@next/third-parties/google'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useStockContext } from '@/app/[locale]/price/stocks/[symbol]/_contexts/StockContext'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { GTM_EVENTS } from '@/configs/dataTrackingEvent'
import { useSsrContext } from '@/contexts/ssr'
import { isUserInRestrictedRegion } from '@/i18n/country'
import { getEventNameFromUrl } from '@/libs/segment/utils'

import styles from './index.module.css'
import OrderTypeSelector, { OrderType } from './OrderTypeSelector'
import PayWith from './PayWith'
import PriceConvertor from './PriceConvertor'
import DownloadAppModal from './PriceConvertor/DownloadAppModal'
import StockListModal from './PriceConvertor/StockListModal'
import TradingTips, { TradingTipsType } from './TradingTips'

interface TradeProps {
  locale: string
}

function Trade({ locale }: TradeProps) {
  const { stock } = useStockContext()
  const { countryCode } = useSsrContext()
  const [conListModalVisible, { open, close }] = useDisclosure(false)
  const [accessAppModalVisible, { open: openAccessModal, close: closeAccessModal }] =
    useDisclosure(false)
  const [orderType, setOrderType] = useState<OrderType>('market')
  const tradable = stock.app_tradable || stock.exchange_tradable
  const t = useTranslations('tradeWidget')

  const tradeButtonClick = () => {
    sendGTMEvent({
      event: GTM_EVENTS.WebBuyButtonClicked,
      path: window.location.pathname,
      stock: stock.symbol,
      entry_channel: getEventNameFromUrl(window.location.pathname),
    })
    openAccessModal()
  }
  const isRestricted = isUserInRestrictedRegion(stock.restricted_areas, countryCode, locale)
  return (
    <>
      <Stack className={styles.tradeContainer} gap={24}>
        <Text component="h2" variant={'heading2'}>
          {t('buyStock', { companyName: stock.name, ticker: stock.symbol })}
        </Text>
        <Flex h={48} align={'center'}>
          <Flex flex={1}>
            <Box className={`${styles.activeTab} ${styles.tabItem}`} mr={12}>
              <Text variant={'heading3'}>{t('buy')}</Text>
            </Box>
            <Box className={styles.tabItem} onClick={tradeButtonClick}>
              <Text variant={'heading3'} c={'var(--content-secondary)'}>
                {t('sell')}
              </Text>
            </Box>
          </Flex>
          <OrderTypeSelector value={orderType} onChange={setOrderType} />
        </Flex>
        <PriceConvertor openCoinListModal={open} />
        {tradable && <PayWith />}
        {!isRestricted && (
          <Button radius={'md'} disabled={!tradable} onClick={tradeButtonClick}>
            {t('buy')} {stock.symbol}
          </Button>
        )}

        {isRestricted && (
          <TradingTips
            symbol={stock.symbol}
            type={TradingTipsType.Restricted}
            openCoinListModal={open}
          />
        )}
        {!tradable && !isRestricted && (
          <TradingTips
            symbol={stock.symbol}
            type={TradingTipsType.UnTradable}
            openCoinListModal={open}
          />
        )}
        {conListModalVisible && <StockListModal visible={conListModalVisible} onClose={close} />}
      </Stack>
      {!isRestricted && tradable && (
        <Center className={styles.responsiveBuyButtonContainer}>
          <Button onClick={tradeButtonClick} h={44} w={'100%'}>
            {t('buy')} {stock.symbol}
          </Button>
        </Center>
      )}
      <DownloadAppModal visible={accessAppModalVisible} onClose={closeAccessModal} />
    </>
  )
}

export default SectionErrorBoundary.with(Trade)
